import { cn } from '@shadcn/lib/utils';
import { cva } from 'class-variance-authority';
import { Iso2 } from 'intl-tel-input/data';
import IntlTelInput from 'intl-tel-input/reactWithUtils';
import 'intl-tel-input/styles';
import { InputHTMLAttributes, useEffect, useState } from 'react';

const Size = {
  small: 'small',
  medium: 'medium',
} as const;

const containerVariants = cva(
  'flex items-center text-teal-dark border rounded-md shadow-sm transition-colors aria-disabled:opacity-50 focus-within:ring-1 focus-within:ring-ring bg-neutral-0 px-[12px]',
  {
    variants: {
      size: {
        [Size.small]: 'text-sm h-[29px]',
        [Size.medium]: 'text-base h-[43px]',
      },
      variant: {
        default: 'border-neutral-400',
        error: 'border-error',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);
type PhoneInputProps = {
  readonly id?: string;
  readonly name?: string;
  readonly autoComplete?: string;
  readonly inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly size?: keyof typeof Size;
  readonly variant?: 'default' | 'error';
  readonly value?: string;
  readonly onChangeNumber: (value: string) => void;
  readonly setIsValidNumber?: (isValid: boolean) => void;
  readonly setIsValidCountry?: (isValid: boolean) => void;
  readonly onBlur?: () => void;
  readonly initialCountry?: Iso2;
  readonly onlyCountries?: Iso2[];
};

const PhoneInput = ({
  autoComplete,
  disabled,
  size = Size.medium,
  variant = 'default',
  value,
  onChangeNumber,
  initialCountry = 'gb',
  onlyCountries = ['gb'],
  setIsValidNumber,
  setIsValidCountry,
  ...props
}: PhoneInputProps) => {
  const [isMounted, setIsMounted] = useState<boolean>(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleChangeCountry = (country: string) => {
    country ? setIsValidCountry?.(true) : setIsValidCountry?.(false);
  };

  const isMultiCountry = onlyCountries?.length !== 1;

  if (!isMounted) return null;

  return (
    <IntlTelInput
      data-test-id="phone-input"
      initOptions={{
        allowDropdown: isMultiCountry,
        containerClass: 'w-full bg-neutral-0 rounded-md',
        initialCountry,
        onlyCountries,
        showFlags: isMultiCountry,
      }}
      initialValue={value ?? ''}
      inputProps={{
        'aria-disabled': disabled,
        autoComplete,
        className: cn(
          'placeholder-abyssyl-blue-light/30 w-full ',
          containerVariants({
            size,
            variant,
          }),
          { 'py-[6px]': size === Size.small },
          { 'py-[12px]': size === Size.medium }
        ),
        disabled,
        ...props,
      }}
      onChangeCountry={handleChangeCountry}
      onChangeNumber={onChangeNumber}
      onChangeValidity={setIsValidNumber}
    />
  );
};

export { PhoneInput, type PhoneInputProps };
